/** @format */

import isEqual from 'lodash.isequal'
import React, {useMemo, useRef} from 'react'

import {CheckboxField} from '@src/components/tailwind/CheckboxV2'
import {
  CloseModalButton,
  ContextModal,
} from '@src/components/tailwind/ModalContext'

import {useFormApi} from '@src/contexts'

import {useTableModel} from './TableModel'

type ColumnConfigModalProps = {
  // the identifier of this modal
  modal: string
}

export function ColumnConfigModal(props: ColumnConfigModalProps) {
  const {modal} = props
  const {updateDisplayConfig} = useFormApi()
  const {
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnVisibility,
    table,
  } = useTableModel()

  // Maintain a separate local state to track column selection changes internally,
  // preventing updates to the table's column visibility state until
  // we explicitly commit the changes.
  const {current: selected} = useRef({...columnVisibility})

  const fields = useMemo(
    () =>
      table
        .getAllColumns()
        .filter(c => c.getCanHide())
        .map(c => c.id),
    [table],
  )

  async function handleConfigChange(): Promise<void> {
    if (isEqual(columnVisibility, selected)) {
      return
    }

    setColumnVisibility({...selected})

    const order = new Set<string>(['_selection', '_status', '_date'])
    for (const f of columnOrder) {
      if (selected[f]) {
        order.add(f)
      }
    }
    for (const [f, visible] of Object.entries(selected)) {
      if (visible) {
        order.add(f)
      }
    }

    // Update columnOrder in case new fields are added
    setColumnOrder([...order])

    // Clean up client-only fields before persisting the order
    for (const c of table.getAllFlatColumns()) {
      if (!c.getCanHide()) {
        order.delete(c.id)
      }
    }
    if (!selected._status) {
      order.delete('_status')
    }

    await updateDisplayConfig({order: [...order]})
  }

  return (
    <ContextModal title="Select columns to display:" modal={modal}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {fields.map(field => (
          <CheckboxField
            defaultChecked={selected[field]}
            key={field}
            // tailwind `break-words` utility class does `overflow-wrap: break-word`
            // which does not do what we want.
            label={<span className="[word-break:break-word]">{field}</span>}
            onCheckedChange={checked => {
              if (checked) {
                selected[field] = true
              } else {
                selected[field] = false
              }
            }}
          />
        ))}
      </div>
      <div className="mt-20 text-right">
        <CloseModalButton
          className="buttonSecondary"
          onClick={handleConfigChange}
        >
          OK
        </CloseModalButton>
      </div>
    </ContextModal>
  )
}
